import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";

import { FullSite, SiteIssue } from "../../../../dtos/site";
import { AjaxResult } from "../../../../enums/ajaxResult";
import { SiteIssueType } from "../../../../enums/SiteIssueType";
import { useApi } from "../../../useApi";

import { SITE_ISSUE_TYPES } from "./consts";

export function useSiteIssues(site: FullSite): {
    issueCount: number;
    issuesByType: { [key in SiteIssueType]: SiteIssue[]; };
    loading: boolean;
    load: () => void;
} {
    const [issues, setIssues] = useState<SiteIssue[]>([]);
    const [loading, setLoading] = useState(false);
    const { siteApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();

    const load = useCallback(() => {
        setLoading(true);
        siteApi.getIssues(site.id).then(({ result, data: loadedIssues }) => {
            if (result === AjaxResult.Success && loadedIssues) {
                setIssues(loadedIssues);
            } else {
                enqueueSnackbar(`Failed to get "${site.name}" issues`, { variant: "error" });
            }
            setLoading(false);
        });
    }, [site, siteApi, enqueueSnackbar]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load]);

    const issuesByType: {
        [key in SiteIssueType]: SiteIssue[];
    } = useMemo(() => {
        const initialObject = SITE_ISSUE_TYPES.reduce((acc, type) => {
            acc[type] = [];
            return acc;
        }, {} as { [key in SiteIssueType]: SiteIssue[] });

        return issues.reduce((acc, issue) => {
            acc[issue.type].push(issue);
            return acc;
        }, initialObject);
    }, [issues]);

    return {
        issueCount: issues.length,
        issuesByType,
        loading,
        load,
    };
}
